import { Warning } from "@mui/icons-material";

import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import _, { capitalize, uniq } from "lodash";
import { formatMoneyString } from "src/utility/utilityFunctions";

import { TableLink, showFirst } from "@components/SmartTable";
import SmartTable, {
  PaginatedResourceProps,
} from "@components/SmartTable/SmartTable";
import { showFirstN } from "@components/SmartTable/helpers";
import { AddressNameLabel } from "@features/addresses";
import { PaperTooltip } from "@features/ui";
import { Order } from "@models";
import { OrderOrigin } from "@models/Order";

import {
  BillableBeaconCost,
  BillableItemCost,
  BillableShippingCost,
  BillableTaxCost,
} from "../BillableCosts";
import {
  getExtendedCost,
  getOrderBrandNames,
  orderTypeLabel,
} from "../helpers";
import { ordersKeyFactory } from "../queries";

type OrderHistoryOrderTableProps = PaginatedResourceProps & {
  data: Order[];
};

const OrderHistoryOrderTable = ({
  data,
  ...tableProps
}: OrderHistoryOrderTableProps) => {
  const queryClient = useQueryClient();

  return (
    <SmartTable
      columns={[
        {
          id: "id",
          label: "Order #",
          sort: "id",
          reverseSort: true,
          render: (id, order) => (
            <div className="flex gap-2">
              {order.orderVariants.some((ov) =>
                ["canceled", "destroyed"].includes(ov.status)
              ) && (
                <PaperTooltip
                  placement="top-start"
                  title="Has Order Items that have been canceled or destroyed"
                >
                  <Warning fontSize="small" />
                </PaperTooltip>
              )}
              <TableLink
                to={`/orders/history/single-order/${id}`}
                onClick={() =>
                  queryClient.setQueryData<Order>(
                    ordersKeyFactory.detail(id).queryKey,
                    order
                  )
                }
              >
                {id}
              </TableLink>
            </div>
          ),
        },
        {
          id: "_anaplanSubCodes",
          label: "12 Digit Code(s)",
          render: (_v, order) =>
            _(order.orderVariants)
              .flatMap((v) => v.anaplanSubProgramCodes)
              .uniq()
              .join(", "),
        },
        {
          id: "_typeOrWindow",
          label: "Order Type / Window",
          render: (_, o) =>
            o.orderingType === "pre-order"
              ? (o.orderWindowName ?? orderTypeLabel(o.orderingType))
              : orderTypeLabel(o.orderingType),
        },
        {
          id: "isForDistributor",
          label: "Origin",
          render: (v) => (v ? OrderOrigin.Distributor : OrderOrigin.Internal),
        },
        {
          id: "address.name",
          label: "Address Name",
          sort: "address-name",
          render: (name, order) => (
            <AddressNameLabel isForReyes={order.address?.isForReyes}>
              {name}
            </AddressNameLabel>
          ),
        },
        { id: "address.abn", label: "ABN" },
        { id: "address.state.code", label: "State" },

        { id: "territoryName", label: "Territory" },
        { id: "programNames", label: "Program", render: showFirst },
        {
          id: "_brands",
          label: "Brand",
          sort: "brand",
          render: (_, order) => showFirstN(getOrderBrandNames(order), 3),
        },
        {
          id: "_brandBus",
          label: "BU",
          render: (_, order) =>
            uniq(
              order.orderVariants.flatMap((v) =>
                v.variant.item.brands.map((b) => b.businessUnit.name)
              )
            ).join(", "),
        },
        {
          id: "submittedAt",
          label: "Order Date",
          sort: "submitted-at",
          reverseSort: true,
          render: (date) => date && format(new Date(date), "MM/dd/yyyy"),
        },
        { id: "totalQuantity", label: "Total Items" },
        {
          id: "totalActualCost",
          label: "Total Product Cost",
          align: "right",
          render: (_, order) => <BillableItemCost costs={order} />,
        },
        {
          id: "totalBeaconCost",
          label: "Total Beacon Cost",
          align: "right",
          render: (_, order) => <BillableBeaconCost costs={order} />,
        },
        {
          id: "totalEstimatedShippingCost",
          label: "Est. Freight",
          align: "right",
          render: (_, order) => <BillableShippingCost costs={order} />,
        },
        {
          id: "totalEstimatedTax",
          label: "Est. Tax",
          align: "right",
          render: (_, order) => <BillableTaxCost costs={order} />,
        },
        {
          id: "_totalExtendedEstCost",
          label: "Est. Total Cost",
          align: "right",
          render: (_, order) => formatMoneyString(getExtendedCost(order)),
        },
        {
          id: "status",
          label: "Status",
          render: (status) =>
            capitalize(status === "submitted" ? "pending" : status),
        },
      ]}
      rows={data}
      {...tableProps}
    />
  );
};

export default OrderHistoryOrderTable;
